import type { ChapterProgress, ProgressMeta } from "@/lib/progress";

const API_PATH = "/api/progress";

export interface UserPayload {
  schemaVersion: 1;
  updatedAt: string;
  email?: string;
  meta: ProgressMeta;
  chapters: Record<string, ChapterProgress>;
  /** Last 20 mock-exam attempts, mirrored from aba-exam-history. Typed
   *  as unknown[] so the push pipeline never needs to assert the schema;
   *  the server trims to the last 20 entries. */
  examHistory?: unknown[];
}

export interface SyncState {
  enabled: boolean;
  email?: string;
  remoteUpdatedAt?: string;
  lastError?: string;
  lastSyncedAt?: string;
}

const STATE_KEY = "aba-sync-state";

export function loadSyncState(): SyncState {
  if (typeof window === "undefined") return { enabled: false };
  try {
    const raw = window.localStorage.getItem(STATE_KEY);
    if (!raw) return { enabled: false };
    const parsed = JSON.parse(raw) as Partial<SyncState>;
    return { enabled: false, ...parsed };
  } catch {
    return { enabled: false };
  }
}

function saveSyncState(state: SyncState) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent("aba-sync-change", { detail: state }));
}

export async function fetchRemoteProgress(signal?: AbortSignal): Promise<UserPayload | null> {
  const res = await fetch(API_PATH, {
    method: "GET",
    credentials: "include",
    signal,
    headers: { Accept: "application/json" },
  });
  if (res.status === 401 || res.status === 403) {
    saveSyncState({ ...loadSyncState(), enabled: false, lastError: "unauthorized" });
    return null;
  }
  if (!res.ok) {
    saveSyncState({ ...loadSyncState(), lastError: `http_${res.status}` });
    throw new Error(`progress fetch failed: ${res.status}`);
  }
  const data = (await res.json()) as UserPayload;
  saveSyncState({
    enabled: true,
    email: data.email,
    remoteUpdatedAt: data.updatedAt,
    lastSyncedAt: new Date().toISOString(),
    lastError: undefined,
  });
  return data;
}

export async function pushRemoteProgress(payload: Omit<UserPayload, "updatedAt" | "schemaVersion" | "email">, signal?: AbortSignal): Promise<UserPayload | null> {
  const body: Omit<UserPayload, "email"> = {
    schemaVersion: 1,
    updatedAt: new Date().toISOString(),
    meta: payload.meta,
    chapters: payload.chapters,
    examHistory: payload.examHistory,
  };
  const res = await fetch(API_PATH, {
    method: "PUT",
    credentials: "include",
    signal,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.status === 401 || res.status === 403) {
    saveSyncState({ ...loadSyncState(), enabled: false, lastError: "unauthorized" });
    return null;
  }
  if (!res.ok) {
    saveSyncState({ ...loadSyncState(), lastError: `http_${res.status}` });
    throw new Error(`progress push failed: ${res.status}`);
  }
  const data = (await res.json()) as { updatedAt: string; email?: string };
  saveSyncState({
    enabled: true,
    email: data.email,
    remoteUpdatedAt: data.updatedAt,
    lastSyncedAt: new Date().toISOString(),
    lastError: undefined,
  });
  return { ...body, email: data.email };
}

export async function clearRemoteProgress(): Promise<boolean> {
  const res = await fetch(API_PATH, { method: "DELETE", credentials: "include" });
  if (!res.ok) return false;
  saveSyncState({ ...loadSyncState(), remoteUpdatedAt: undefined, lastSyncedAt: new Date().toISOString() });
  return true;
}

let pushTimer: number | undefined;
let pendingPush: Omit<UserPayload, "updatedAt" | "schemaVersion" | "email"> | undefined;

export function schedulePush(payload: Omit<UserPayload, "updatedAt" | "schemaVersion" | "email">, delayMs = 1500) {
  pendingPush = payload;
  if (typeof window === "undefined") return;
  if (pushTimer) window.clearTimeout(pushTimer);
  pushTimer = window.setTimeout(async () => {
    const snapshot = pendingPush;
    pendingPush = undefined;
    pushTimer = undefined;
    if (!snapshot) return;
    try {
      await pushRemoteProgress(snapshot);
    } catch {
      // sync error already recorded; localStorage remains the source of truth offline.
    }
  }, delayMs) as unknown as number;
}
